import { ComplianceProgramCalibrationType } from "types/graphql-schema";

import { useProgramsSection } from "@apps/programs/programs-section-context";
import { currentUserVar, isAdminVar } from "@cache/cache";
import Callout from "@components/callout/callout";
import AppLink from "@components/link/link";

import { ProgramForm } from "../types";
import useProgramFormLoadPreview from "../use-program-form-load-preview";

/**
 * Says when one person alone can change the review or unblock its
 * pre-calibration (TF-1692). A note, not an error: it never blocks Publish.
 *
 * Shown as a warning under the Summary, because it is about who runs the review
 * rather than how heavy it is. Nothing shows while there is nothing to warn
 * about.
 */
const ProgramFormWhoCanStepIn = ({
  proposedComplianceProgram: form,
}: {
  proposedComplianceProgram: ProgramForm;
}) => {
  const { noun } = useProgramsSection();
  const nounLower = noun.toLowerCase();
  const { preview, waitingForASelection } = useProgramFormLoadPreview(form);
  // While the rule names nobody the request is not sent, and the answer still
  // held is about the selection before it.
  if (!preview || waitingForASelection) {
    return null;
  }
  const editor = preview.soleEditor;
  const unblocker =
    form.calibration === ComplianceProgramCalibrationType.PreCalibration
      ? preview.solePreCalibrationUnblocker
      : null;
  if (!editor && !unblocker) {
    return null;
  }
  // Only someone who can change the review opens this form, so a sole editor is
  // the viewer, and is then also the only admin.
  const text =
    editor && unblocker
      ? `Only you can change this ${nounLower} and mark people as calibrated. If you are away, managers stay blocked. Add a people admin, so someone can step in.`
      : editor
        ? `Only you can change this ${nounLower}. Add a people admin, so someone can step in when you are away.`
        : unblocker?.id === currentUserVar().id
          ? "Only you can mark people as calibrated. If you are away, managers stay blocked."
          : `Only ${unblocker?.name} can mark people as calibrated. If they are away, managers stay blocked.`;
  return (
    <Callout icon="warning" size="small" theme="warning">
      {text}
      {/* Only an admin can open Members, where the people admin role is given. */}
      {isAdminVar() && (
        <>
          {" "}
          <AppLink
            className="text-blue-600 hover:underline"
            to={`/settings/organization/${form.organizationId}/members`}
          >
            Manage members
          </AppLink>
        </>
      )}
    </Callout>
  );
};

export default ProgramFormWhoCanStepIn;
